import { useEffect, useRef, useState } from "react";
import { batchApi } from "@services/batchApi";
import { isBatchTerminal } from "@models/batch";
import type { BatchJobStatusResponse } from "@models/batch";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_MS = 30 * 60 * 1000; // 30 minutes

interface BatchPollState {
  response: BatchJobStatusResponse | null;
  polling: boolean;
  error: string | null;
}

/**
 * Polls a single batch job until it reaches a terminal state.
 * Pass null jobId to disable.
 */
export const useBatchPoll = (jobId: string | null): BatchPollState => {
  const [response, setResponse] = useState<BatchJobStatusResponse | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef<number>(0);

  useEffect(() => {
    if (!jobId) {
      setResponse(null);
      setPolling(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    startedAt.current = Date.now();
    setPolling(true);
    setError(null);
    setResponse(null);

    const tick = async (): Promise<void> => {
      try {
        const data = await batchApi.getJobStatus(jobId);
        if (cancelled) return;
        setResponse(data);

        const terminal = isBatchTerminal(data.global_status);
        const timedOut = Date.now() - startedAt.current > MAX_POLL_MS;

        if (terminal || timedOut) {
          setPolling(false);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to poll job status");
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId]);

  return { response, polling, error };
};
